import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import platformConfigService from '../services/platformConfigService';
import { runMigrationIfNeeded } from '../utils/migrateLocalStorageToFirebase';

export interface SocialMediaPlatform {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  icon: string;
  accessToken?: string;
  refreshToken?: string;
  connected: boolean;
  locked?: boolean; // Plataformas que aún no están disponibles
}

interface UseSocialMediaPlatformsReturn {
  platforms: SocialMediaPlatform[];
  activePlatforms: SocialMediaPlatform[];
  loading: boolean;
  error: string | null;
  updatePlatform: (platformId: string, updates: Partial<SocialMediaPlatform>) => Promise<void>;
  savePlatformSettings: () => Promise<void>;
  loadPlatformSettings: () => Promise<void>;
}

const defaultPlatforms: SocialMediaPlatform[] = [
  {
    id: 'instagram',
    name: 'Instagram',
    enabled: false,
    description: 'Plataforma de fotos y videos',
    icon: '📸',
    connected: false
  },
  {
    id: 'facebook',
    name: 'Facebook',
    enabled: false,
    description: 'Red social más grande del mundo',
    icon: '📘',
    connected: false,
    locked: true
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    enabled: false,
    description: 'Plataforma de videos cortos',
    icon: '🎵',
    connected: false,
    locked: true
  }
];

export const useSocialMediaPlatforms = (): UseSocialMediaPlatformsReturn => {
  const { user } = useAuth();
  const [platforms, setPlatforms] = useState<SocialMediaPlatform[]>(defaultPlatforms);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPlatformSettings = useCallback(async () => {
    if (!user) {
      setPlatforms(defaultPlatforms);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ejecutar migración si es necesaria
      await runMigrationIfNeeded(user.uid);

      // Cargar configuraciones desde Firebase
      const savedConfigs = await platformConfigService.getSocialMediaConfigs();
      
      if (savedConfigs && savedConfigs.length > 0) {
        // Convertir de formato Firebase a formato del hook
        const convertedPlatforms = savedConfigs.map(config => ({
          id: config.id,
          name: config.name,
          enabled: config.enabled,
          description: config.description,
          icon: config.icon,
          connected: config.connected,
          accessToken: config.accessToken,
          refreshToken: config.refreshToken
        }));
        setPlatforms(convertedPlatforms);
        console.log('✅ Configuraciones de redes sociales cargadas desde Firebase:', convertedPlatforms);
      } else {
        console.log('📝 No hay configuraciones guardadas, usando valores por defecto');
        setPlatforms(defaultPlatforms);
      }
    } catch (err) {
      console.error('❌ Error cargando configuraciones de redes sociales:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
      setPlatforms(defaultPlatforms);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const savePlatformSettings = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Convertir plataformas a formato Firebase
      const firebaseConfigs = platforms.map(platform => ({
        id: platform.id,
        name: platform.name,
        enabled: platform.enabled,
        description: platform.description,
        icon: platform.icon,
        connected: platform.connected,
        accessToken: platform.accessToken,
        refreshToken: platform.refreshToken,
        lastUpdated: new Date() // Agregar timestamp actual
      }));

      // Guardar en Firebase
      await platformConfigService.updateSocialMediaConfigs(firebaseConfigs);
      console.log('✅ Configuraciones de redes sociales guardadas en Firebase:', firebaseConfigs);

      // Simular preparación de integraciones para redes sociales
      await prepareSocialMediaIntegrations();
      
    } catch (err) {
      console.error('❌ Error guardando configuraciones de redes sociales:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [user, platforms]);

  const prepareSocialMediaIntegrations = async () => {
    const enabledPlatforms = platforms.filter(p => p.enabled);
    
    console.log('🔧 Preparando integraciones para redes sociales activas:', enabledPlatforms.map(p => p.name));
    
    // Preparar integraciones para redes sociales habilitadas
    for (const platform of enabledPlatforms) {
      if (!platform.connected) {
        try {
          // Simulamos credenciales básicas para la demostración
          const credentials = {
            access_token: `mock_access_token_${platform.id}_${Date.now()}`,
            refresh_token: `mock_refresh_token_${platform.id}_${Date.now()}`
          };

          // Simular preparación de integración
          console.log(`🔗 Preparando integración para ${platform.name}...`);
          
          setPlatforms(prev => 
            prev.map(p => 
              p.id === platform.id 
                ? { ...p, connected: true, accessToken: credentials.access_token, refreshToken: credentials.refresh_token }
                : p
            )
          );
          console.log(`✅ Integración preparada para ${platform.name}`);
          
        } catch (error) {
          console.error(`❌ Error preparando integración para ${platform.name}:`, error);
        }
      }
    }

    // Deshabilitar integraciones para redes sociales deshabilitadas
    const disabledPlatforms = platforms.filter(p => !p.enabled && p.connected);
    for (const platform of disabledPlatforms) {
      try {
        console.log(`🔌 Deshabilitando integración para ${platform.name}...`);
        
        setPlatforms(prev => 
          prev.map(p => 
            p.id === platform.id 
              ? { ...p, connected: false, accessToken: undefined, refreshToken: undefined }
              : p
          )
        );
        console.log(`✅ Integración deshabilitada para ${platform.name}`);
        
      } catch (error) {
        console.error(`❌ Error deshabilitando integración para ${platform.name}:`, error);
      }
    }
  };

  const updatePlatform = useCallback(async (platformId: string, updates: Partial<SocialMediaPlatform>) => {
    setPlatforms(prev => 
      prev.map(platform => 
        platform.id === platformId 
          ? { ...platform, ...updates }
          : platform
      )
    );
  }, []);

  useEffect(() => {
    // Solo cargar una vez al inicializar
    loadPlatformSettings();
  }, []); // Sin dependencias para que solo se ejecute una vez

  // Solo las redes sociales que están habilitadas Y conectadas están listas para publicar
  const activePlatforms = platforms.filter(platform => platform.enabled && platform.connected);

  return {
    platforms,
    activePlatforms,
    loading,
    error,
    updatePlatform,
    savePlatformSettings,
    loadPlatformSettings
  };
};
